/**
 * HepcoGfxRailProfile — cross-section of the Hepco GFX guiding rail used by
 * the procedural extrusion in <HepcoGfxRail>.
 *
 * The profile is expressed in the Beckhoff path-frame convention:
 *   - path tangent = +X (extrusion direction)
 *   - lateral      = ±Y (left/right of the path)
 *   - vertical     = ±Z (Z-up, path origin at Z=0)
 *
 * THREE.ExtrudeGeometry maps a Shape's local X to the curve's Frenet normal
 * and Shape's local Y to the binormal. For a planar curve in the world XY
 * plane the initial Frenet frame computed by three.js is:
 *   normal   = (0, 0, -1)     -> shape.x maps to world  -Z
 *   binormal = lateral in XY  -> shape.y maps to world  ±Y
 * To keep this module's public dimensions in natural "Z-up" coordinates,
 * the Shape factory negates Z when emitting shape.x. Callers should think
 * in (lateral Y, vertical Z); the negation is an implementation detail.
 *
 * The Hepco GFX cross-section is TWO disconnected regions:
 *
 *  1. Baseplate — a wide flat plate at the bottom (134.45 mm × 30.1 mm).
 *     One per track; the two plates of an XTS oval sit side-by-side and
 *     together span the full ~268.9 mm system width.
 *
 *  2. Rail — a separate spear-profile bar (40 mm × 20 mm) sitting on top
 *     of the baseplate at its +Y edge. Has horizontally pointed apexes on
 *     both lateral sides; the mover's V-rollers engage these apexes.
 *
 * Two shape factories are exported so the consumer can extrude each region
 * as its own mesh; merging them into a single Shape would require a thin
 * connecting sliver that misrepresents the real geometry.
 *
 * The `stackYOffsetMm` / `stackZOffsetMm` fields shift both regions
 * together — used by the playground calibration UI to fine-tune position
 * relative to the path origin without changing the inherent geometry.
 */

import { Shape } from 'three';

export interface HepcoGfxRailProfileDims {
  /** Baseplate Y-extent per track (mm). Half of the XTS oval's full
   *  cross-section width. */
  readonly baseplateWidthMm: number;
  /** Baseplate Z-extent (mm). */
  readonly baseplateThicknessMm: number;
  /** Z-coordinate of the baseplate's top face in the path frame (before
   *  stackZOffsetMm is applied). Also the Z of the rail's bottom face. */
  readonly baseplateTopZMm: number;
  /** Rail Y-extent, spear-apex to spear-apex (mm). */
  readonly railWidthMm: number;
  /** Rail Z-extent, main-body top to main-body bottom (mm). */
  readonly railHeightMm: number;
  /** Z-coordinate of the rail's main-body top in the path frame (before
   *  stackZOffsetMm is applied). */
  readonly railTopZMm: number;
  /** Lateral protrusion of each spear apex beyond the rail's main body (mm). */
  readonly spearProtrusionMm: number;
  /** Half-height of the spear at its inner (main-body-side) edge (mm) —
   *  measured from the apex Z toward both top and bottom shoulders. */
  readonly spearBaseHalfHeightMm: number;
  /** Concave-undercut inset on the rail's bottom face just inboard of
   *  each spear (mm) — the rail's bottom edge is shorter than its top
   *  edge by 2× this value. */
  readonly undercutInsetMm: number;
  /** Calibration shift in Y applied to baseplate + rail together (mm). */
  readonly stackYOffsetMm: number;
  /** Calibration shift in Z applied to baseplate + rail together (mm). */
  readonly stackZOffsetMm: number;
}

/**
 * Scaffolding dimensions (mm). The stack offsets bake in the user-
 * requested calibration so the rendered geometry is correct out of the
 * box; the playground / a `*.meta.json` sidecar can override them.
 */
export const HEPCO_GFX_PROFILE: HepcoGfxRailProfileDims = Object.freeze({
  baseplateWidthMm: 134.45,
  baseplateThicknessMm: 30.1,
  baseplateTopZMm: -45,
  railWidthMm: 44,
  railHeightMm: 15,
  railTopZMm: -30,
  spearProtrusionMm: 5,
  spearBaseHalfHeightMm: 4,
  undercutInsetMm: 3,
  stackYOffsetMm: -10,
  stackZOffsetMm: 0,
});

/**
 * Merge a partial profile override onto the default profile. Used by the
 * playground / sidecar loader so callers only need to supply the fields
 * they want to override.
 */
export function resolveHepcoGfxProfile(
  override?: Partial<HepcoGfxRailProfileDims>,
): HepcoGfxRailProfileDims {
  if (!override) return HEPCO_GFX_PROFILE;
  return Object.freeze({ ...HEPCO_GFX_PROFILE, ...override });
}

/**
 * Build the baseplate cross-section (mm). Simple rectangle positioned so
 * the rail's +Y spear apex sits flush with the baseplate's +Y edge; the
 * baseplate extends in -Y direction from there. Both axes are shifted by
 * `stackYOffsetMm` / `stackZOffsetMm` before being emitted.
 */
export function createHepcoGfxBaseplateShape(
  dims: HepcoGfxRailProfileDims = HEPCO_GFX_PROFILE,
): Shape {
  const sx = (zWorld: number): number => -(zWorld + dims.stackZOffsetMm);
  const sy = (yWorld: number): number => yWorld + dims.stackYOffsetMm;

  const innerY = dims.railWidthMm / 2;
  const outerY = innerY - dims.baseplateWidthMm;
  const topZ = dims.baseplateTopZMm;
  const bottomZ = topZ - dims.baseplateThicknessMm;

  const s = new Shape();
  s.moveTo(sx(bottomZ), sy(outerY));   // bottom-outer
  s.lineTo(sx(bottomZ), sy(innerY));   // bottom-inner
  s.lineTo(sx(topZ),    sy(innerY));   // top-inner
  s.lineTo(sx(topZ),    sy(outerY));   // top-outer
  s.closePath();
  return s;
}

/**
 * Build the rail cross-section (mm). Symmetric spear-profile bar centered
 * on Y=0 (before stackYOffsetMm). The mover's V-rollers grip the two
 * spear apexes (at Y=±railWidth/2, Z=midZ).
 *
 * The profile's top edge is flush with the spear shoulder top — there is
 * NO rectangular section above the spear shoulders. `railHeightMm` therefore
 * spans only from the main-body bottom up to the spear shoulder top.
 */
export function createHepcoGfxRailShape(
  dims: HepcoGfxRailProfileDims = HEPCO_GFX_PROFILE,
): Shape {
  const sx = (zWorld: number): number => -(zWorld + dims.stackZOffsetMm);
  const sy = (yWorld: number): number => yWorld + dims.stackYOffsetMm;

  const halfWidth = dims.railWidthMm / 2;
  const mainHalfWidth = halfWidth - dims.spearProtrusionMm;
  const bottomHalfWidth = mainHalfWidth - dims.undercutInsetMm;

  // Rail top = spear shoulder top. The bottom-rectangle / undercut still
  // sit at zBottom = railTopZ − railHeight; the spear apex is half-way
  // between zBottom and (zSpearTop + spearBaseHalfHeight). Computing zMid
  // from those keeps the apex visually centered between the shoulders.
  const zSpearTop = dims.railTopZMm;
  const zBottom = zSpearTop - dims.railHeightMm;
  const zSpearBot = zSpearTop - 2 * dims.spearBaseHalfHeightMm;
  const zMid = zSpearTop - dims.spearBaseHalfHeightMm;

  const s = new Shape();
  // CCW in (Y, Z), starting top-left at the +Y/−Y shoulder top edge.
  s.moveTo(sx(zSpearTop), sy(-mainHalfWidth));   // 1: top-left (=−Y shoulder top)
  s.lineTo(sx(zSpearTop), sy(+mainHalfWidth));   // 2: top-right (=+Y shoulder top)
  s.lineTo(sx(zMid),      sy(+halfWidth));       // 3: +Y spear apex
  s.lineTo(sx(zSpearBot), sy(+mainHalfWidth));   // 4: +Y spear shoulder bottom
  s.lineTo(sx(zBottom),   sy(+bottomHalfWidth)); // 5: undercut → bottom-right
  s.lineTo(sx(zBottom),   sy(-bottomHalfWidth)); // 6: bottom-left after undercut
  s.lineTo(sx(zSpearBot), sy(-mainHalfWidth));   // 7: −Y spear shoulder bottom
  s.lineTo(sx(zMid),      sy(-halfWidth));       // 8: −Y spear apex
  s.closePath();
  return s;
}
